/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import CutWireFieldsTable from "@eisinc/App/Components/CutWire/CutWireFields";
import Notify from "@eisinc/App/Components/Notify";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import AddOutline from "@eisinc/XDL/Icons/AddOutline";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import Button from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    isOpen: state.cutWireModal.isOpen,
    line: state.cutWireModal.cartLine,
});

export interface CutLineModalStyles {
    modalStyles?: ModalPresentationProps;
    headingText?: TypographyProps;
    productWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    productImage?: ProductImageStyles;
    productLeftSide?: InjectableCss;
    productDescription?: InjectableCss;
    bottomLine?: InjectableCss;
    addCutButton?: LinkPresentationProps;
}

const styles: CutLineModalStyles = {
    headingText: {
        size: "16px",
        lineHeight: "24px",
        css: css`
            color: #383d40;
            margin-bottom: 15px;
            display: block;
        `,
    },
    productWrapper: {
        css: css`
            display: flex;
            padding: 12px 0;
            margin-bottom: 20px;
            border-top: 1px solid ${getColor("common.ui7")};
            border-bottom: 1px solid ${getColor("common.ui7")};
            overflow: hidden;
        `,
    },
    imageWrapper: {
        css: css`
            width: 63px;
            padding-right: 10px;
        `,
    },
    productImage: {
        linkWrappingImage: {
            css: css`
                width: 53px;
                height: 53px;
            `,
        },
        image: {
            css: css`
                width: auto;
                height: auto;
                max-width: 53px;
            `,
        },
    },
    productLeftSide: {
        css: css`
            width: calc(100% - 63px);
        `,
    },
    productDescription: {
        css: css`
            .product-description {
                font-size: 16px;
                line-height: 19px;
                text-overflow: ellipsis;
                overflow: hidden;
                white-space: nowrap;
            }
        `,
    },
    bottomLine: {
        css: css`
            margin-top: 16px;
            padding: 0 50px 0 64px;
            display: flex;
            justify-content: space-between;

            @media (max-width: 481px) {
                padding: 0;
                flex-wrap: wrap;
            }
        `,
    },
    addCutButton: {
        color: "primary.main",
        typographyProps: {
            size: "14px",
            lineHeight: "24px",
        },
        icon: {
            iconProps: {
                src: AddOutline,
                size: 16,
                color: "primary.main",
                css: css`
                    margin-right: 5px;
                    vertical-align: middle;
                `,
            },
            position: "left",
        },
        css: css`
            margin-top: 0;
            @media (max-width: 481px) {
                width: 105px;
            }
        `,
    },
};

const mapDispatchToProps = {
    updateCartLine,
};

export interface CutLengthsLine {
    cutCount: string;
    cutValue: string;
    total: string;
    uid?: string;
}

type OwnProps = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const CutWireModal: FC<OwnProps> = ({ isOpen, line, updateCartLine }) => {
    if (!isOpen || !line) {
        return null;
    }

    const [cutObject, setCutObject] = useState([] as any);
    const [remaining, setRemaining] = useState(0);
    const [extendedTotal, setExtendedTotal] = useState("0");
    const [totalExceeded, setTotalExceeded] = useState(false);
    const dispatch = useAppDispatch();

    useEffect(() => {
        // eslint-disable-next-line no-prototype-builtins
        if (line.properties?.hasOwnProperty("cutWire") && line.properties["cutWire"] !== "") {
            const cutObject = JSON.parse(line.properties["cutWire"]);
            setCutObject(cutObject);
            const extendedTotal = cutObject.reduce(function (prev: any, current: any) {
                return prev + parseInt(current.total, 10);
            }, 0);
            setExtendedTotal(extendedTotal.toString());
            const remaining = line.qtyOrdered! - parseInt(extendedTotal, 10);
            setRemaining(remaining > 0 ? remaining : 0);
        } else {
            const newCutLine: CutLengthsLine = {
                cutCount: "0",
                cutValue: "0",
                total: "0",
            };
            const initialCutObject = [] as any;
            // adding three default rows
            initialCutObject.push({ ...newCutLine, uid: uniqueId() });
            initialCutObject.push({ ...newCutLine, uid: uniqueId() });
            initialCutObject.push({ ...newCutLine, uid: uniqueId() });
            setCutObject(initialCutObject);
            setExtendedTotal("0");
            setRemaining(line.qtyOrdered!);
        }
    }, []);

    useEffect(() => {
        // on last row delete the length will already be zero so we have to include it in the count
        if (cutObject.length >= 0) {
            const newExtendedTotal = cutObject.reduce(function (prev: any, current: any) {
                return prev + parseInt(current.total, 10);
            }, 0);
            setExtendedTotal(newExtendedTotal.toString());
            setTotalExceeded(newExtendedTotal > line.qtyOrdered!);
            const remaining = line.qtyOrdered! - parseInt(newExtendedTotal, 10);
            setRemaining(remaining > 0 ? remaining : 0);
        }
    }, [cutObject]);

    const handleCloseModal = () => {
        dispatch({
            type: "CutWire/SetModalClosed",
            isOpen: false,
            cartLine: {} as CartLineModel,
        });
    };

    const increaseCutRowCount = () => {
        // add new row
        const newCutLine: CutLengthsLine = {
            cutCount: "0",
            cutValue: "0",
            total: "0",
            uid: uniqueId(),
        };
        const newCutObject = [...cutObject];
        newCutObject.push(newCutLine);
        setCutObject(newCutObject);
    };

    const handleSaveCutLengths = () => {
        const total = parseInt(extendedTotal, 10);
        const cartLine = line;
        if (total >= line.qtyOrdered! && cutObject.length > 0) {
            const differentialQty = total - line.qtyOrdered!;
            const qtyOrdered = line.qtyOrdered! + differentialQty;
            // removing zero values from the object
            const clearedCutWire = cutObject
                .filter((cutRow: CutLengthsLine) => cutRow.total !== "0")
                .map(({ cutCount, cutValue, total }: CutLengthsLine) => ({ cutCount, cutValue, total }));
            const cutWire = JSON.stringify(clearedCutWire);
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    qtyOrdered,
                    properties: {
                        ...cartLine.properties,
                        cutWire,
                    },
                },
                onSuccess: () => {
                    // set the updated line in the store
                    dispatch({
                        type: "CutWire/SetModalOpened",
                        isOpen: false,
                        cartLine: {},
                    });
                    window.scrollTo(0, 0);
                },
            });
        } else {
            // on deleting all cutRows
            const cutWire = "";
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    properties: {
                        ...cartLine.properties,
                        cutWire,
                    },
                },
                onSuccess: () => {
                    // set the updated line in the store
                    dispatch({
                        type: "CutWire/SetModalOpened",
                        isOpen: false,
                        cartLine: {},
                    });
                },
            });
        }
    };

    const isAddCutRowDisabled = () => {
        return cutObject.some((cutLine: CutLengthsLine) => cutLine.cutValue === "0" || cutLine.cutCount === "0");
    };

    return (
        <Modal
            {...styles.modalStyles}
            headline={translate("Cut Lengths")}
            size={600}
            isOpen={isOpen}
            handleClose={handleCloseModal}
        >
            <Typography {...styles.headingText} as="span">
                {siteMessage("Custom_CutLengthsModalHeadingText")}
            </Typography>
            <StyledWrapper {...styles.productWrapper}>
                <StyledWrapper {...styles.imageWrapper}>
                    <ProductImage product={line} extendedStyles={styles.productImage} />
                </StyledWrapper>
                <StyledWrapper {...styles.productLeftSide}>
                    <StyledWrapper {...styles.productDescription}>
                        {/* eslint-disable react/no-danger */}
                        <p
                            className="product-description"
                            dangerouslySetInnerHTML={{
                                __html: line.shortDescription,
                            }}
                        />
                    </StyledWrapper>
                    <LineNumbers
                        manufacturerItem={line.manufacturerItem}
                        customerName={line.customerName}
                        specNumber={line.properties["SpecNumber"]}
                        erpNumber={line.erpNumber}
                    />
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper>
                <CutWireFieldsTable
                    unitOfMeasureDescription={line.unitOfMeasureDescription}
                    lineId={line.id}
                    cutObject={cutObject}
                    setCutObject={setCutObject}
                    cartLine={line}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.bottomLine}>
                <StyledWrapper
                    css={css`
                        width: calc(100% / 3);
                    `}
                >
                    <Link disabled={isAddCutRowDisabled()} {...styles.addCutButton} onClick={increaseCutRowCount}>
                        {translate("Add Cut")}
                    </Link>
                </StyledWrapper>
                <StyledWrapper
                    css={css`
                        width: calc(100% / 3);
                        display: flex;
                    `}
                >
                    <Typography
                        css={css`
                            width: 105px;
                            display: block;
                            @media (max-width: 481px) {
                                width: 99px;
                            }
                            @media (max-width: 350px) {
                                width: 93px;
                            }
                        `}
                    >
                        {translate("Total Cuts")}
                    </Typography>
                    <Typography as="span">=</Typography>
                </StyledWrapper>
                <StyledWrapper
                    css={css`
                        width: calc(100% / 3);
                        display: flex;
                        white-space: nowrap;
                    `}
                >
                    <Typography
                        as="span"
                        css={css`
                            margin-right: 15px;
                            @media (max-width: 481px) {
                                padding-left: 12px;
                            }
                        `}
                    >
                        {extendedTotal}
                    </Typography>
                    <Typography
                        as="span"
                        color="#CA0000"
                        weight="bold"
                        css={css`
                            @media (max-width: 481px) {
                                display: none;
                            }
                        `}
                    >{`(${remaining} ${translate("Remaining")})`}</Typography>
                </StyledWrapper>
                <StyledWrapper
                    css={css`
                        width: 100%;
                        display: none;
                        @media (max-width: 481px) {
                            display: block;
                            text-align: center;
                        }
                    `}
                >
                    <Typography as="span" color="#CA0000" weight="bold">{`(${remaining} ${translate(
                        "Remaining",
                    )})`}</Typography>
                </StyledWrapper>
            </StyledWrapper>
            {totalExceeded && (
                <Notify
                    notifyContainerOverrides={css`
                        margin-top: 10px;
                    `}
                    messageType="warning"
                    showCloseButton={true}
                    body={
                        /* eslint-disable react/no-danger */
                        <div
                            dangerouslySetInnerHTML={{
                                __html: siteMessage("Cart_CutLengthsQtyUpdated") as any,
                            }}
                        ></div>
                    }
                />
            )}
            {!totalExceeded && remaining !== 0 && cutObject.length > 0 && (
                <Notify
                    notifyContainerOverrides={css`
                        margin-top: 10px;
                    `}
                    messageType="warning"
                    showCloseButton={true}
                    body={
                        /* eslint-disable react/no-danger */
                        <div
                            dangerouslySetInnerHTML={{
                                __html: siteMessage("Cart_CutLengthsLessThanQtyOrdred") as any,
                            }}
                        ></div>
                    }
                />
            )}
            <StyledWrapper className="modal-actions">
                <StyledWrapper
                    css={css`
                        font-size: 12px;
                        line-height: 16px;
                        flex-grow: 1;
                        padding-right: 10px;
                        @media (max-width: 481px) {
                            margin-bottom: 10px;
                        }
                    `}
                >
                    <div
                        dangerouslySetInnerHTML={{
                            __html: siteMessage("Custom_CutLengthModalDislcaimer") as any,
                        }}
                    />
                </StyledWrapper>
                <Button variant="secondary" onClick={handleCloseModal}>
                    {translate("Cancel")}
                </Button>
                <Button
                    variant="primary"
                    disabled={!totalExceeded && remaining !== 0 && cutObject.length > 0}
                    onClick={handleSaveCutLengths}
                >
                    {translate("Save")}
                </Button>
            </StyledWrapper>
        </Modal>
    );
};

interface LineProps {
    specNumber?: string;
    erpNumber?: string;
    manufacturerItem?: string;
    customerName?: string;
}

// to show only the first two non-empty values
const LineNumbers = ({ specNumber, erpNumber, manufacturerItem, customerName }: LineProps) => {
    const mapShow = {
        showMFR: !customerName || (customerName && !specNumber) || (!customerName && !specNumber),
        showERP:
            (!customerName && !specNumber) ||
            (!customerName && !manufacturerItem) ||
            (!specNumber && !manufacturerItem) ||
            (!customerName && !manufacturerItem && !specNumber),
    };
    return (
        <StyledWrapper
            css={css`
                display: flex;
                justify-content: flex-start;
                font-size: 12px;
                line-height: 24px;
            `}
        >
            {customerName && (
                <StyledWrapper
                    css={css`
                        margin-right: 10px;
                    `}
                >
                    <Typography as="strong">{siteMessage("Custom_Your_Part_#", "Your Part #:")}</Typography>
                    <Typography as="span">{customerName}</Typography>
                </StyledWrapper>
            )}
            {specNumber && (
                <StyledWrapper
                    css={css`
                        margin-right: 10px;
                    `}
                >
                    <Typography as="strong">{siteMessage("Custom_Spec_#", "Spec #:")}</Typography>
                    <Typography as="span">{specNumber}</Typography>
                </StyledWrapper>
            )}
            {manufacturerItem && mapShow.showMFR && (
                <StyledWrapper
                    css={css`
                        margin-right: 10px;
                    `}
                >
                    <Typography as="strong">{siteMessage("Custom_MFR_#", "MFR #:")}</Typography>
                    <Typography as="span">{manufacturerItem}</Typography>
                </StyledWrapper>
            )}
            {erpNumber && mapShow.showERP && (
                <StyledWrapper>
                    <Typography as="strong">{siteMessage("Custom_Part_#", "EIS Part #:")}</Typography>
                    <Typography as="span">{erpNumber}</Typography>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(CutWireModal);
